import React, { useState, useContext } from "react"
import { AlertsContext } from "../../../utils/providers/alerts"
import { funEU } from "../../../utils/firebase"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Input from "../../ui/inputs/input"
import Textarea from "../../ui/inputs/textarea"
import Button from "../../ui/button/button"
import RecipientPicker from "../recipient-picker/recipient-picker"

const createPaymentLinkCallable = funEU.httpsCallable("createPaymentLink")

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Modal for creating + sending a Stripe Payment Link.
 *
 * Mirrors server-side validation in `functions-payments/src/payment-links.ts`:
 *   - payee required
 *   - amount > 0, ≤ outstanding, max 2dp, real number
 *   - recipient email valid when "send to lead booker" is off
 *
 * Calls `createPaymentLink` callable on submit; the webhook handles the
 * post-payment flow. We close the modal on success and rely on the snapshot
 * listener in the Payments tab to surface the new link.
 */
export default function SendLink({ bookingID, outstanding, leadBooker, close, prefill }) {
	const { pushAlert } = useContext(AlertsContext)

	const [payee, setPayee] = useState(prefill?.payee || "")
	const [amount, setAmount] = useState(
		typeof prefill?.amount === "number" ? prefill.amount.toFixed(2) : "",
	)
	const [note, setNote] = useState("")
	const [useLeadBooker, setUseLeadBooker] = useState(
		typeof prefill?.useLeadBooker === "boolean" ? prefill.useLeadBooker : Boolean(leadBooker?.email),
	)
	const [alternateEmail, setAlternateEmail] = useState(prefill?.alternateEmail || "")
	const [sending, setSending] = useState(false)
	const [errors, setErrors] = useState({})

	const outstandingNumber = Number(outstanding) || 0
	const setToOutstandingDisabled = outstandingNumber <= 0

	const handleAmountChange = (raw) => {
		// Allow empty, digits, optional single decimal point with up to 2 dp.
		// Don't reformat as the user types — just block invalid characters
		// so they can backspace freely.
		if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
			setAmount(raw)
		}
	}

	const handleSetToOutstanding = () => {
		setAmount(outstandingNumber.toFixed(2))
	}

	const validate = () => {
		const nextErrors = {}

		if (!payee.trim()) {
			nextErrors.payee = "Payee name is required."
		}

		const amountNum = Number(amount)
		if (!amount.trim()) {
			nextErrors.amount = "Amount is required."
		} else if (!Number.isFinite(amountNum)) {
			nextErrors.amount = "Amount must be a number."
		} else if (amountNum <= 0) {
			nextErrors.amount = "Amount must be greater than zero."
		} else if (amountNum > outstandingNumber + 0.001) {
			nextErrors.amount = `Amount can't exceed outstanding £${formatGbp(outstandingNumber)}.`
		}

		if (useLeadBooker) {
			if (!leadBooker?.email) {
				nextErrors.recipient = "Lead booker has no email on file. Untick and enter an address."
			}
		} else {
			const trimmed = alternateEmail.trim()
			if (!trimmed) {
				nextErrors.recipient = "Recipient email is required."
			} else if (!EMAIL_RE.test(trimmed)) {
				nextErrors.recipient = "That doesn't look like a valid email address."
			}
		}

		if (note.length > 500) {
			nextErrors.note = "Note is too long (max 500 characters)."
		}

		setErrors(nextErrors)
		return Object.keys(nextErrors).length === 0
	}

	const submit = async () => {
		if (!validate()) return

		setSending(true)

		try {
			await createPaymentLinkCallable({
				bookingID,
				amount: Number(amount),
				payee: payee.trim(),
				note: note.trim() || undefined,
				useLeadBooker,
				alternateEmail: useLeadBooker ? undefined : alternateEmail.trim(),
			})

			pushAlert({
				type: "SUCCESS",
				title: "Payment link sent",
			})
			close()
		} catch (err) {
			const message = err?.message || "Could not send payment link."
			pushAlert({
				type: "ERROR",
				title: message,
			})
			setSending(false)
		}
	}

	return (
		<Window
			title="Send payment link"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className="input-grid cols-2">
					<div className="span-2">
						<Input
							type="text"
							placeholder="Payee name:"
							value={payee}
							onChange={setPayee}
							error={errors.payee}
						/>
						{errors.payee && <p style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>{errors.payee}</p>}
					</div>

					<div className="span-2">
						<div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
							<div style={{ flex: 1 }}>
								<Input
									type="text"
									inputMode="decimal"
									placeholder="Amount:"
									symbol="£"
									value={amount}
									onChange={handleAmountChange}
									error={errors.amount}
								/>
							</div>
							<Button
								label="Set to outstanding"
								small={true}
								onClick={handleSetToOutstanding}
								disabled={setToOutstandingDisabled}
							/>
						</div>
						{errors.amount && <p style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>{errors.amount}</p>}
					</div>

					<div className="span-2">
						<Textarea
							type="text"
							placeholder="Optional note for the email (visible to the recipient):"
							value={note}
							onChange={setNote}
						/>
						{errors.note && <p style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>{errors.note}</p>}
					</div>

					<div className="span-2">
						<RecipientPicker
							leadBookerEmail={leadBooker?.email}
							leadBookerLabel={leadBooker?.email}
							useLeadBooker={useLeadBooker}
							onToggle={(next) => {
								setUseLeadBooker(next)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							alternateEmail={alternateEmail}
							onAlternateChange={(value) => {
								setAlternateEmail(value)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							error={errors.recipient}
							checkboxLabel="Send payment link to lead booker"
						/>
					</div>

					<div className="span-2">
						<Button
							label="Create &amp; send payment link"
							fullWidth={true}
							loading={sending}
							loadingText="Sending..."
							onClick={submit}
						/>
					</div>
				</div>
			</WindowCard>
		</Window>
	)
}
